import { useRef, useState, type FC } from 'react';
import {
  Button,
  RadioTile,
  InlineLoading,
  InlineNotification,
  Loading,
} from '@carbon/react';
import {
  CheckmarkOutline,
  CheckmarkFilled,
  Close,
  Edit,
  Information,
  MagicWand,
  OrderDetails,
  Renew,
  SettingsAdjust,
  Time,
  TrashCan,
} from '@carbon/react/icons';
import classNames from 'classnames';
import { formatNumber } from 'utils/number';
import { formatTcWorkspaceDate } from 'utils/dates';
import type {
  PreDrillWorkspace,
  PreDrillTargetZoneSuggestions,
} from 'types/api-responses';
import { TargetZoneSuggestionTile } from './target-zone-suggestion-tile';
import { TargetZoneUserSelectedTile } from './target-zone-user-selected-tile';
import { AgentOutputTable } from './agent-output-table';
import { RerunAgentModal } from './rerun-agent-modal';
import { formatSrpWellName } from './utils';
import styles from './target-zone-identification.module.scss';

interface TargetZoneIdentificationProps {
  workspace: PreDrillWorkspace;
  suggestions: PreDrillTargetZoneSuggestions | null;
  loading: boolean;
  /** Verdict is "correct": drives the Mark/Remove toggle + locked tiles. */
  markedCorrect: boolean;
  /** Confirm / remove in flight (disables the toggle). */
  busy: boolean;
  rerunning: boolean;
  /** Mark as Correct: confirm the selected interval. `manualOverride` marks the
   *  analyst's own pick vs an AI candidate (both can share an interval id). */
  onConfirm: (geologicIntervalId: number, manualOverride: boolean) => void;
  /** Remove Correct Status: revert the target-zone verdict. */
  onRemoveCorrect: () => void;
  onRerunAgent: () => void;
  /** Get AI Suggestions: fire the first agent run from the neutral prompt. AI
   *  never runs on load; the analyst opts in here. */
  onGetAiSuggestions: () => void;
  /** Open the Change Match drawer to manually pick a geologic interval. */
  onOpenChangeMatch: () => void;
  /** True when there's a committed selection/suggestion to clear (BE-gated). */
  clearable: boolean;
  /** Clear Selection in flight. */
  clearing: boolean;
  onClearSelection: () => void;
  onOpenAgentOutput: () => void;
  onOpenAgentRuns: () => void;
  onOpenGlossary: () => void;
  /**
   * Story 8: the bore has no directional survey, so confirming a zone is what will
   * give it a geometry. Only the geometry tearsheet knows this — it reads the points
   * — so the pre-drill workspace omits it and shows nothing, as it always has.
   */
  noDirectionalSurvey?: boolean;
  /** Read the opt-in state off the manual pick rather than the zone id: a Well
   *  Workspace bore carries its geometry's zone before anyone picked one (UAT 18).
   *  The geometry sheet only; the pre-drill page keys on the zone id. */
  neutralOnPick?: boolean;
}

// Read a string field off the agent's inputs snapshot.
function snapshotText(
  snapshot: Record<string, unknown>,
  key: string
): string | null {
  const value = snapshot[key];
  return typeof value === 'string' ? value : null;
}

// Read a numeric field off the agent's inputs snapshot.
function snapshotNumber(
  snapshot: Record<string, unknown>,
  key: string
): number | null {
  const value = snapshot[key];
  return typeof value === 'number' ? value : null;
}

// Target Zone Identification (Match Review): Permit doc tile + AI-suggestion
// RadioTiles + Confirm. Confirming commits the zone server-side (cascade + regen).
export const TargetZoneIdentification: FC<TargetZoneIdentificationProps> = ({
  workspace,
  suggestions,
  loading,
  markedCorrect,
  busy,
  rerunning,
  onConfirm,
  onRemoveCorrect,
  onRerunAgent,
  onGetAiSuggestions,
  onOpenChangeMatch,
  clearable,
  clearing,
  onClearSelection,
  onOpenAgentOutput,
  onOpenAgentRuns,
  onOpenGlossary,
  noDirectionalSurvey = false,
  neutralOnPick = false,
}) => {
  const candidates = suggestions?.candidates ?? [];
  // Analyst-picked zone (Change Match): rendered as its own "User Selected" tile
  // alongside the AI suggestions whenever the pick is a manual override.
  const userSelected = suggestions?.userSelected ?? null;
  const userSelectedTile =
    userSelected && userSelected.manualOverride ? userSelected : null;
  // Selection carries both the interval and its source (manual pick vs AI
  // candidate) — the id alone can't say which, since tiles can share one.
  const [selection, setSelection] = useState<{
    id: number;
    manual: boolean;
  } | null>(null);
  const [rerunModalOpen, setRerunModalOpen] = useState(false);

  const editable = workspace.auth.includes('update');
  // (Re)generating: an actual agent run is in flight (via `rerunning`). A bare
  // `pending` status is neutral, not a run signal (so Clear returns to neutral).
  const regenerating = rerunning || workspace.targetZoneStatus === 'processing';
  // Locked while marked correct: tiles disable, header flips to Remove status.
  const confirmed = markedCorrect;

  // Default selection: the confirmed interval (manual or AI), else the top
  // candidate. Local `selection` wins once the analyst picks a tile.
  const effectiveSelection =
    selection ??
    (workspace.targetZoneGeologicIntervalId !== null
      ? {
          id: workspace.targetZoneGeologicIntervalId,
          manual: !!userSelectedTile,
        }
      : candidates[0]
        ? { id: candidates[0].geologicIntervalId, manual: false }
        : null);
  const effectiveSelectedId = effectiveSelection
    ? String(effectiveSelection.id)
    : '';
  const effectiveSelectedManual = effectiveSelection?.manual ?? false;
  const confirmedName =
    candidates.find((c) => String(c.geologicIntervalId) === effectiveSelectedId)
      ?.geologicIntervalName ??
    (userSelected &&
    String(userSelected.geologicIntervalId) === effectiveSelectedId
      ? userSelected.name
      : null);

  // Neutral / opt-in state: no candidates, no manual pick, nothing in flight. Read off
  // the run and the pick, not the zone id: a Well Workspace bore carries its geometry's
  // zone before anyone picked one (UAT 18).
  const neutral =
    !loading &&
    !regenerating &&
    candidates.length === 0 &&
    (neutralOnPick
      ? !userSelectedTile
      : workspace.targetZoneGeologicIntervalId === null);

  const snapshot = suggestions?.inputsSnapshot ?? {};

  // Permit on File — the live permit backing the workspace, served on the
  // suggestions read whenever a permit_header exists (source of truth for tile).
  const permit = suggestions?.permitOnFile ?? null;

  const ft = (value: number | null | undefined) => {
    return typeof value === 'number' ? `${formatNumber(value, 0, 0)} ft` : null;
  };
  const basinField =
    [permit?.basin, permit?.field].filter(Boolean).join(' · ') || null;

  const permitLabel =
    formatSrpWellName(permit?.lease, permit?.wellNum) ??
    formatSrpWellName(
      snapshotText(snapshot, 'wellName'),
      snapshotText(snapshot, 'wellNum')
    ) ??
    workspace.uwi14;
  const permitFields = [
    { label: 'Lease', value: permit?.lease ?? null },
    { label: 'Well Number', value: permit?.wellNum ?? null },
    { label: 'Licensee', value: permit?.licensee ?? null },
    { label: 'Hole Direction', value: permit?.holeDirection ?? null },
    { label: 'Target Formation', value: permit?.targetFormation ?? null },
    { label: 'Target Fluid', value: permit?.targetFluid ?? null },
    { label: 'Permitted MD', value: ft(permit?.permittedDepthFt) },
    { label: 'Permitted TVD', value: ft(permit?.permittedDepthTvdFt) },
    {
      label: 'License Date',
      value: permit?.licenseDate
        ? formatTcWorkspaceDate(permit.licenseDate)
        : null,
    },
    { label: 'License Num', value: permit?.licenseNum ?? null },
    { label: 'License Status', value: permit?.licenseStatus ?? null },
    { label: 'Basin / Field', value: basinField },
  ];

  // Suggestions are stale if the workspace's selected grid collection differs
  // from the snapshot's — user must re-run the agent before marking correct.
  const snapshotGridId = snapshotNumber(snapshot, 'structureGridCollectionId');
  const suggestionsStale =
    !!suggestions &&
    snapshotGridId !== null &&
    workspace.structureGridCollectionId !== null &&
    snapshotGridId !== workspace.structureGridCollectionId;

  const handleConfirm = () => {
    if (!effectiveSelection) return;
    onConfirm(effectiveSelection.id, effectiveSelection.manual);
  };

  // Clicking a tile focuses its radio, which scrolls the whole document and
  // hides the header — capture scroll on pointer-down, restore it after focus.
  const preClickScrollRef = useRef<{ top: number; left: number } | null>(null);

  const rememberScrollForPointer = () => {
    const root = document.documentElement;
    preClickScrollRef.current = { top: root.scrollTop, left: root.scrollLeft };
  };

  const restoreScrollAfterFocus = () => {
    const saved = preClickScrollRef.current;
    if (!saved) return;
    preClickScrollRef.current = null;
    const root = document.documentElement;
    if (root.scrollTop !== saved.top) root.scrollTop = saved.top;
    if (root.scrollLeft !== saved.left) root.scrollLeft = saved.left;
  };

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <p className={styles.eyebrow}>AI Agent</p>
          <div className={styles.titleRow}>
            <h2 className={styles.title}>Target Zone Identification</h2>
            {confirmed && (
              <span className={styles.workflowStatus}>
                <CheckmarkOutline className={styles.workflowStatusIcon} />
                Complete
              </span>
            )}
          </div>
        </div>
        <div className={styles.headerActions}>
          <Button
            kind="danger--tertiary"
            size="sm"
            hasIconOnly
            renderIcon={TrashCan}
            iconDescription="Clear Selection"
            tooltipPosition="bottom"
            disabled={!editable || !clearable || clearing || regenerating}
            onClick={onClearSelection}
          />
          {/* Manual pick but the agent hasn't run yet — offer Get AI
              Suggestions here (Rerun's slot once candidates exist). */}
          {candidates.length === 0 && userSelectedTile && (
            <Button
              kind="tertiary"
              size="sm"
              hasIconOnly
              renderIcon={MagicWand}
              iconDescription="Get AI Suggestions"
              tooltipPosition="bottom"
              disabled={!editable || regenerating}
              onClick={onGetAiSuggestions}
            />
          )}
          {/* Rerun only exists once the agent has produced candidates — in the
              neutral state the analyst uses "Get AI Suggestions" instead. */}
          {candidates.length > 0 && (
            <Button
              kind="tertiary"
              size="sm"
              hasIconOnly
              renderIcon={Renew}
              iconDescription="Rerun the agent"
              tooltipPosition="bottom"
              // Disabled while (re)generating: rerun in flight or job pending.
              disabled={!editable || regenerating}
              onClick={() => setRerunModalOpen(true)}
            />
          )}
          <Button
            kind="tertiary"
            size="sm"
            hasIconOnly
            renderIcon={SettingsAdjust}
            iconDescription="Change Match"
            tooltipPosition="bottom"
            // Locked while marked correct — mirror the RadioTile lock.
            disabled={!editable || markedCorrect}
            onClick={onOpenChangeMatch}
          />
          <Button
            kind="tertiary"
            size="sm"
            hasIconOnly
            renderIcon={OrderDetails}
            iconDescription="Agent Details"
            tooltipPosition="bottom"
            disabled={!suggestions}
            onClick={onOpenAgentOutput}
          />
          {markedCorrect ? (
            <Button
              kind="tertiary"
              size="sm"
              renderIcon={Close}
              disabled={!editable || busy}
              onClick={onRemoveCorrect}
            >
              Remove Correct Status
            </Button>
          ) : (
            <Button
              kind="primary"
              size="sm"
              renderIcon={CheckmarkOutline}
              disabled={
                !editable || !effectiveSelectedId || busy || suggestionsStale
              }
              onClick={handleConfirm}
            >
              Mark as Correct
            </Button>
          )}
        </div>
      </div>

      <div className={styles.body}>
        {/* Says why the zone matters here: with no survey on file, the confirmation
            is what produces the geometry rather than merely labelling one. */}
        {noDirectionalSurvey && (
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton
            className={styles.surveyBanner}
            title="No directional survey on file."
            subtitle="Confirming a Target Zone will generate a derived wellbore geometry from the existing wellbore points based on the active Structure Grid Collection."
          />
        )}
        {suggestionsStale && !rerunning && (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            title="Structure grid collection changed"
            subtitle="Re-run the agent to refresh the target-zone suggestions before marking it correct."
          />
        )}

        {confirmed && confirmedName && (
          <div className={styles.confirmedBanner}>
            <CheckmarkFilled className={styles.confirmedBannerIcon} />
            <span>
              <strong>Confirmed Target Zone</strong> — {confirmedName}
            </span>
          </div>
        )}

        <div className={styles.sectionHeadingRow}>
          <p className={styles.sectionLabel}>Match Review</p>
          {/* Scoring Glossary / Agent Runs are agent-run artifacts — hide them
              in the neutral state where no run has happened yet. */}
          {!neutral && (
            <div className={styles.sectionLinks}>
              <Button
                kind="ghost"
                size="sm"
                renderIcon={Information}
                onClick={onOpenGlossary}
              >
                Scoring Glossary
              </Button>
              <Button
                kind="ghost"
                size="sm"
                renderIcon={Time}
                onClick={onOpenAgentRuns}
              >
                Agent Runs
              </Button>
            </div>
          )}
        </div>
        <p className={styles.sectionDesc}>
          Select a target zone from the geologic-interval catalog, or use{' '}
          <strong>Get AI Suggestions</strong> for ranked candidates. The
          confirmed zone drives the pre-drill landing geometry.
        </p>

        {regenerating && (
          <div className={styles.regenBanner}>
            <Loading
              small
              withOverlay={false}
              className={styles.regenSpinner}
            />
            <span className={styles.regenText}>
              <span className={styles.regenTitle}>
                Rerunning agent suggestions
              </span>
              <span className={styles.regenSubtitle}>
                Agent suggestions are being regenerated. This may take a moment.
              </span>
            </span>
          </div>
        )}

        {loading ? (
          <InlineLoading description="Loading suggestions…" />
        ) : (
          // Two-column Match Review: Permit doc tile beside the Target Zone
          // column. Scroll capture/restore stops tile focus shifting the page.
          <div
            className={styles.tiles}
            onMouseDownCapture={rememberScrollForPointer}
            onFocus={restoreScrollAfterFocus}
          >
            <div className={styles.tileDocPane}>
              <div className={styles.tileDoc}>
                <p className={styles.tileEyebrow}>Permit on File</p>
                <p className={styles.tileName}>{permitLabel}</p>
                <dl className={styles.tileFields}>
                  {permitFields.map((field) => (
                    <div key={field.label} className={styles.tileField}>
                      <dt>{field.label}</dt>
                      <dd>{field.value ?? '—'}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            {neutral ? (
              <div className={styles.targetZoneEmpty}>
                <p className={styles.tileEyebrow}>Target Zone</p>
                <div className={styles.targetZoneEmptyBody}>
                  <p className={styles.targetZoneEmptyTitle}>
                    No target zone selected
                  </p>
                  <p className={styles.targetZoneEmptyDesc}>
                    Select a pre-drill landing zone to continue. Choose one
                    manually from the geologic-interval catalog, or have the
                    agent surface ranked suggestions to pick from.
                  </p>
                </div>
                <div className={styles.targetZoneEmptyActions}>
                  <Button
                    kind="primary"
                    size="md"
                    renderIcon={Edit}
                    disabled={!editable}
                    onClick={onOpenChangeMatch}
                  >
                    Select Target Zone
                  </Button>
                  <Button
                    kind="tertiary"
                    size="md"
                    renderIcon={MagicWand}
                    disabled={!editable}
                    onClick={onGetAiSuggestions}
                  >
                    Get AI Suggestions
                  </Button>
                </div>
              </div>
            ) : candidates.length > 0 || userSelectedTile ? (
              // AI suggestions and the manual pick share one row: ranked
              // candidates, then the "User Selected" tile appended after them.
              <div className={styles.tileMatches}>
                {candidates.map((candidate, index) => {
                  const value = String(candidate.geologicIntervalId);
                  // AI candidate is selected only via an AI-source pick — a
                  // same-interval manual pick belongs to its own tile.
                  const isSelected =
                    effectiveSelection?.id === candidate.geologicIntervalId &&
                    !effectiveSelectedManual;
                  const isConfirmed =
                    confirmed &&
                    !workspace.targetZoneManualOverride &&
                    candidate.geologicIntervalId ===
                      workspace.targetZoneGeologicIntervalId;
                  return (
                    <RadioTile
                      key={candidate.geologicIntervalId}
                      id={`target-zone-${candidate.geologicIntervalId}`}
                      name="pre-drill-target-zone"
                      value={value}
                      checked={isSelected}
                      disabled={!editable || markedCorrect}
                      className={classNames(styles.tileMatch, {
                        [styles.tileMatchConfirmed]: isConfirmed,
                      })}
                      onChange={() =>
                        setSelection({
                          id: candidate.geologicIntervalId,
                          manual: false,
                        })
                      }
                    >
                      <TargetZoneSuggestionTile
                        candidate={candidate}
                        index={index}
                      />
                    </RadioTile>
                  );
                })}
                {userSelectedTile && (
                  <RadioTile
                    // Distinct id from any same-interval AI candidate tile, so
                    // the duplicate pick doesn't collide on a DOM id.
                    id={`target-zone-user-selected-${userSelectedTile.geologicIntervalId}`}
                    name="pre-drill-target-zone"
                    value={String(userSelectedTile.geologicIntervalId)}
                    // The manual pick is the selection only via its own source —
                    // never because an AI candidate of the same interval is picked.
                    checked={
                      effectiveSelection?.id ===
                        userSelectedTile.geologicIntervalId &&
                      effectiveSelectedManual
                    }
                    disabled={!editable || markedCorrect}
                    className={classNames(styles.tileMatch, {
                      [styles.tileMatchConfirmed]:
                        confirmed &&
                        workspace.targetZoneManualOverride &&
                        userSelectedTile.geologicIntervalId ===
                          workspace.targetZoneGeologicIntervalId,
                    })}
                    onChange={() =>
                      setSelection({
                        id: userSelectedTile.geologicIntervalId,
                        manual: true,
                      })
                    }
                  >
                    <TargetZoneUserSelectedTile zone={userSelectedTile} />
                  </RadioTile>
                )}
              </div>
            ) : regenerating ? (
              // Agent run in flight with nothing to show yet — a loading
              // placeholder, not the "no suggestion" empty state.
              <div className={styles.empty}>
                <InlineLoading description="Generating suggestions…" />
              </div>
            ) : (
              <p className={styles.empty}>
                No AI suggestion is available for this permit yet.
              </p>
            )}
          </div>
        )}

        {!neutral && <AgentOutputTable uwi14={workspace.uwi14} />}
      </div>

      <RerunAgentModal
        open={rerunModalOpen}
        permitLabel={permitLabel}
        loading={rerunning}
        onClose={() => setRerunModalOpen(false)}
        onConfirm={() => {
          onRerunAgent();
          setRerunModalOpen(false);
        }}
      />
    </section>
  );
};
